import type { ReactNode } from "react";

import type { IcIcon } from "@jetblue/kit-icons/types";
import { List } from "@jetblue/kit/list";
import { cn } from "../../utilities/tailwind";

export type ListItemProperties = {
  /**
   * Accepts an icon as a function (from our icon library, e.g. `IcCheckbox`).
   */
  Icon?: IcIcon | undefined;
  className?: string | undefined;
  /**
   * The `data` is the content displayed in each list item.
   */
  data: ReactNode;
  /**
   * The `decorator` is more flexible than the `Icon` and can be any `ReactNode`. The `decorator` takes precedence over the `Icon`.
   */
  decorator?: ReactNode | undefined;
  /**
   * When this boolean is `true`, a margin-top of .5rem is added to the list item.
   */
  hasMarginTop?: boolean | undefined;
  /**
   * Allows specialized styling of the `Icon`.
   */
  iconClassName?: string | undefined;
  /**
   * The id should be unique and is used with the `key` property when when React maps over the given list items.
   */
  id: string;
  /**
   * If a `ListItem` is provided an `items` array, it will nest another `List` within that list item.
   */
  items?: ListItemProperties[] | undefined;
};
export const ListItem = ({
  Icon,
  className = "",
  data,
  decorator,
  hasMarginTop,
  iconClassName = "",
  items,
  ...properties
}: ListItemProperties) => {
  const lineItemTwClassName = cn(
    "list-item",
    { "mt-2": hasMarginTop },
    className,
  );

  return (
    <li className={lineItemTwClassName} {...properties}>
      <div className="flex items-center">
        {Boolean(decorator ?? Icon) && (
          <span className="mx-4">
            {decorator ??
              (Icon && (
                <Icon
                  aria-label="bullet"
                  className={iconClassName}
                  role="img"
                />
              ))}
          </span>
        )}

        {data}
      </div>

      {items && <List Icon={Icon} className="my-4 ms-8" items={items} />}
    </li>
  );
};
ListItem.displayName = "List.Item";
